// Firebase v11 Modular API
import {
  User, UserCredential, Auth as FirebaseAuth,
  signInWithEmailAndPassword, signInAnonymously,
  createUserWithEmailAndPassword, EmailAuthProvider, signInWithCustomToken,
  signInWithPopup, signInWithRedirect, getRedirectResult,
  reauthenticateWithCredential, updatePassword,
  OAuthProvider, GoogleAuthProvider, sendPasswordResetEmail, sendEmailVerification,
  linkWithCredential, signOut, onAuthStateChanged, reload,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { getApp } from 'firebase/app';
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { UserService } from "./user.service";
import { ToastController } from "@ionic/angular";
import { Platform } from "@ionic/angular";
import { AnalyticsService } from "./analytics.service";
import { PurchaseService } from "./purchase.service";
import { DailyStreakService } from "./daily-streak.service";
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Browser } from "@capacitor/browser";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { FirebaseInitService } from './firebase-init.service';

/**
 * Service that handles authentication, registration and password-resets.
 * Should only do requests to Firebase.
 */
@Injectable({
  providedIn: "root"
})
export class AuthService {
  public fireUser?: User;
  public reauth: boolean = false;
  public anon: boolean = false;
  public codeVerifier: string;
  private isRegistering: boolean = false; // Race Condition Protection
  private cachedNativeToken?: string;
  private cachedTokenExpiry?: number;

  /**
   * Returns if the user is logged in.
   */
  get isLoggedIn(): boolean {
    return !!this.fireUser && !this.isAnonymous;
  }

  /**
   * Return if the users email is verified.
   */
  get isEmailVerified(): boolean {
    return !!this.fireUser?.emailVerified;
  }

  /**
   * Return if the user is signed-in as anonymous.
   */
  get isAnonymous(): boolean {
    return !!this.fireUser?.isAnonymous;
  }

  /**
   * Return if the user is signed-in via social provider.
   */
  get isSocial(): boolean {
    return !!this.fireUser?.displayName;
  }

  private firebaseAuth: FirebaseAuth | null = null;

  public async ensureNativeUserLoaded(): Promise<void> {
    // Wenn schon da → nichts tun
    if (this.fireUser) {
      console.log('✅ [AuthService] ensureNativeUserLoaded: fireUser already set:', this.fireUser.uid);
      return;
    }
  
    console.log('🔍 [AuthService] ensureNativeUserLoaded: fireUser is empty, checking native user...');
    
    try {
      const current = await FirebaseAuthentication.getCurrentUser();
      console.log('🔍 [AuthService] getCurrentUser result in ensureNativeUserLoaded:', current);
  
      if (current?.user) {
        const u = current.user;
        console.log('✅ [AuthService] Native user found in ensureNativeUserLoaded:', u.uid);
  
        // Fake-Web-User bauen (wie bei signInWithEmail)
        const webUser: any = {
          uid: u.uid,
          email: u.email,
          displayName: u.displayName,
          photoURL: u.photoUrl,
          emailVerified: u.emailVerified,
          isAnonymous: false,
          providerData: u.providerData || []
        };
  
        this.fireUser = webUser;
        this.setUserInInterceptor(this.fireUser);
  
        // Backend-User laden, aber Fehler dürfen NICHT alles kaputt machen
        try {
          await this.userService.setCurrentUser(this.fireUser);
          console.log('✅ [AuthService] Backend user loaded in ensureNativeUserLoaded');
        } catch (backendErr) {
          console.warn('⚠️ [AuthService] Backend user loading failed in ensureNativeUserLoaded:', backendErr);
          // WICHTIG: fireUser NICHT löschen, wir sind trotzdem eingeloggt
        }
      } else {
        console.log('ℹ️ [AuthService] ensureNativeUserLoaded: no native user found');
      }
    } catch (err) {
      console.warn('⚠️ [AuthService] ensureNativeUserLoaded: getCurrentUser failed:', err);
    }
  }  

  private async getFirebaseAuth(): Promise<FirebaseAuth> {
    if (!this.firebaseAuth) {
      await this.firebaseInit.waitForReady();
  
      const app = getApp();
      console.log('Firebase app is initialized:', app.name);
  
      const isIOS   = this.platform.is('ios');
      const hasCordova = typeof (window as any).cordova !== 'undefined';
      const isIOSCordova = isIOS && hasCordova;
  
      let savedCordova: any = null;
      if (isIOSCordova) {
        console.log('🩹 [AuthService] Hiding window.cordova for Firebase Auth (use browser mode)');
        savedCordova = (window as any).cordova;
        delete (window as any).cordova;
      }
  
      try {
        // FIXED: Use persistent storage for native apps too
        // The native Firebase SDK handles its own persistence automatically,
        // but we still need to configure the web Firebase Auth instance properly
        // for cases where web auth is used as fallback
        const persistence = [indexedDBLocalPersistence, browserLocalPersistence];

        console.log('🧱 [AuthService] initializeAuth with persistence: indexedDB + localStorage');

        this.firebaseAuth = initializeAuth(app, {
          persistence,
          popupRedirectResolver: browserPopupRedirectResolver,
        });

        console.log('✅ [AuthService] initializeAuth() completed');
      } catch (error: any) {
        // Wenn Auth bereits initialisiert wurde, verwende getAuth() stattdessen
        if (error?.code === 'auth/already-initialized') {
          console.log('ℹ️ [AuthService] Auth already initialized, using getAuth()');
          this.firebaseAuth = getAuth(app);
        } else {
          console.error('❌ [AuthService] initializeAuth failed:', error);
          throw error;
        }
      } finally {
        if (isIOSCordova && savedCordova) {
          (window as any).cordova = savedCordova;
          console.log('🩹 [AuthService] window.cordova restored after Firebase init');
        }
      }
    }
  
    return this.firebaseAuth!;
  }
  
  private setUserInInterceptor(user: User | null): void {
    // Set the fireUser for the AuthService
    this.fireUser = user || undefined;
    
    if (user) {
      console.log("User set in AuthService:", user.uid);
      // Don't navigate here - let the user$ subscription handle navigation
      // The navigation happens when user$ emits with the backend user data
    } else {
      console.log("User cleared from AuthService");
    }
  }

  constructor(
    public readonly userService: UserService,
    public readonly purchaseService: PurchaseService,
    public readonly router: Router,
    public toastController: ToastController,
    public platform: Platform,
    private analyticsService: AnalyticsService,
    public dailyStreak: DailyStreakService,
    public http: HttpClient,
    private firebaseInit: FirebaseInitService
  ) {
    // Make AuthService accessible globally for UserService on iOS
    (window as any).__authService = this;
    
    // Firebase Auth will be initialized when needed
    // Subscribe to the authState to hold the user within this service.
    this.userService.user$.subscribe(user => {
      if (user == null) {
        return;
      }
      else if (this.router.url.includes("reauth")) {
        this.reauth = true;
      }
      else if (this.router.url.includes("anon")) {
        this.anon = true;
      }
      if (user.role === "Admin") {
        // Use safe Firebase call
        // Note: setAnalyticsCollectionEnabled is not available in modular API
        // Analytics collection is controlled via Firebase console
        this.analyticsService.MPOptOut();
        this.analyticsService.GAOptOut();
        this.router.navigateByUrl("tabs/dashboard", {replaceUrl: true});
      } else if (user) {
        //User is not an admin, not anonymously logged in, not reauthenticating
        const currentUrl = this.router.url;
        const baseUrl = currentUrl.split("?")[0] || "/";
        const isExerciseRoute = baseUrl.includes("/exercise");

        const shouldNavigate = !isExerciseRoute && !this.reauth && !this.anon && (
          baseUrl.endsWith("/start") || 
          baseUrl.endsWith("/login") || 
          baseUrl.endsWith("/register") ||
          baseUrl === "/start" ||
          baseUrl === "/login" ||
          baseUrl === "/register"
        );
        
        console.log('🔍 Navigation check:', {
          currentUrl,
          baseUrl,
          shouldNavigate,
          isExerciseRoute,
          reauth: this.reauth,
          anon: this.anon,
          userRole: user.role
        });
        
        if (shouldNavigate) {
          const urlTree = this.router.parseUrl(currentUrl);
          const rawReturnUrl = urlTree.queryParams?.['returnUrl'];
          let safeReturnUrl: string | null = null;

          if (rawReturnUrl) {
            try {
              const decodedReturnUrl = decodeURIComponent(rawReturnUrl);
              const normalizedReturnUrl = decodedReturnUrl.trim();

              if (
                normalizedReturnUrl.startsWith("/") &&
                !normalizedReturnUrl.startsWith("//")
              ) {
                safeReturnUrl = normalizedReturnUrl;
              } else {
                console.warn('⚠️ Ignoring unsafe returnUrl:', rawReturnUrl);
              }
            } catch (error) {
              console.warn('⚠️ Failed to decode returnUrl:', rawReturnUrl, error);
            }
          }

          if (safeReturnUrl) {
            console.log('✅ Navigating to returnUrl:', safeReturnUrl);
            this.router.navigateByUrl(safeReturnUrl, { replaceUrl: true });
          } else {
            console.log('✅ Navigating to tabs/home');
            this.router.navigate(["tabs/home"], { replaceUrl: true });
          }
        }

        //User is logged in with an account
        if(user.role == "User")
        {
          this.purchaseService.init(user.uid);
        }
      }
    });
    this.initializeAuthStateListener();
  }

  /**
   * Initialize Firebase Auth state listener to restore user session on app startup
   */
  private async initializeAuthStateListener(): Promise<void> {
    try {
      console.log('🔧 [AuthService] Starting auth state listener initialization...');
        
      await this.firebaseInit.waitForReady();
      console.log('✅ [AuthService] Firebase is ready');
        
      const isCapacitor = this.platform.is('capacitor');
      const isCordova  = this.platform.is('cordova');
      const isAndroid  = this.platform.is('android');
      const isIOS      = this.platform.is('ios');
        
      console.log('🔍 [AuthService] Platform detection:', {
        isCapacitor,
        isCordova,
        isAndroid,
        isIOS,
        userAgent: navigator.userAgent
      });
        
      const isNativeApp = isCapacitor || isCordova;
  
      if (isNativeApp) {
        console.log('📱 [AuthService] Platform: Native (Android/iOS) → using NativeAuthStateListener');
        await this.initializeNativeAuthStateListener();
      } else if (isIOS) {
        console.log('📱 [AuthService] Platform detected: iOS (using Web SDK auth listener)');
        await this.initializeWebAuthStateListener();
      } else {
        console.log('🌐 [AuthService] Platform: Web → using WebAuthStateListener');
        await this.initializeWebAuthStateListener();
      }
    
      console.log('✅ [AuthService] Firebase Auth state listener initialized');
    } catch (error) {
      console.error('❌ [AuthService] Failed to initialize Firebase Auth state listener:', error);
    }
  }
  

  /**
   * Initialize auth state listener for web platform
   */
  private async initializeWebAuthStateListener(): Promise<void> {
    const auth = await this.getFirebaseAuth();
    
    // Listen for auth state changes
    onAuthStateChanged(auth, async (user) => {
      console.log('🔥 Auth state changed (all platforms):', user ? user.uid : 'no user');
    
      if (user) {
        this.setUserInInterceptor(user);
        this.fireUser = user;
    
        try {
          this.userService.setCurrentUser(user);
          console.log('✅ Backend user loaded and user$ emitted');
        } catch (error) {
          console.error('❌ Error loading backend user:', error);
        }
      } else {
        this.fireUser = undefined;
        this.setUserInInterceptor(null);
        this.userService.clearUser();  // <── HIER
        console.log('User logged out (user$ cleared)');
      }    
    });
    
  }

  /**
   * Initialize auth state listener for native (Capacitor) platform
   */
  private async initializeNativeAuthStateListener(): Promise<void> {
    // For native platforms, periodically check auth state
    // since onAuthStateChanged is not available in Capacitor Firebase
    
    // CRITICAL: Check immediately when app starts (don't wait for interval)
    // This ensures login persistence works properly on Android and iOS
    const checkAuthState = async () => {
      try {
        const current = await FirebaseAuthentication.getCurrentUser();
        if (current?.user) {
          // User is logged in
          if (!this.fireUser || this.fireUser.uid !== current.user.uid) {
            console.log('🔥 [AuthService] Native Auth state: User logged in:', current.user.uid);
            this.fireUser = {
              uid: current.user.uid,
              email: current.user.email,
              displayName: current.user.displayName,
              photoURL: current.user.photoUrl,
              emailVerified: current.user.emailVerified,
              isAnonymous: false
            } as any;
            
            // CRITICAL: Set user in interceptor FIRST before making API calls
            console.log('👤 [AuthService] Setting user in interceptor...');
            this.setUserInInterceptor(this.fireUser);
            
            // Load backend user data and emit via user$ observable
            // This is critical for persisting login state on Android and iOS
            try { 
              await this.userService.setCurrentUser(this.fireUser);
              console.log('✅ [AuthService] Backend user loaded successfully on app restart');
            } catch (error) {
              console.error('❌ [AuthService] Error loading backend user on app restart:', error);
              // Don't clear fireUser here - user is still logged in on Firebase
              // The backend error might be temporary (network issue, etc.)
            }
          } else {
            // User already set and hasn't changed, skip backend call
          }
        } else {
          // User is logged out
          if (this.fireUser) {
            console.log('🔥 [AuthService] Native Auth state: User logged out');
            this.fireUser = undefined;
            this.setUserInInterceptor(null);
            this.userService.clearUser();
          }
        }
      } catch (error) {
        console.warn('⚠️ [AuthService] Error checking native auth state:', error);
        // Don't throw error - retry on next interval
      }
    };
    
    // Check immediately on app start (with retry on failure)
    let retries = 0;
    const maxRetries = 3;
    while (retries < maxRetries) {
      try {
        await checkAuthState();
        console.log('✅ [AuthService] Initial auth state check completed');
        break;
      } catch (error) {
        retries++;
        console.warn(`⚠️ [AuthService] Initial auth state check failed (attempt ${retries}/${maxRetries}):`, error);
        if (retries < maxRetries) {
          // Wait 1 second before retry
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    
    // Then check periodically every 5 seconds (reduced frequency to avoid overhead)
    setInterval(checkAuthState, 5000);
  }

// Replace your debugRawEmailLogin method with this enhanced version

public async debugRawEmailLogin(email: string, password: string): Promise<void> {
  console.log("🐛 [AuthService] debugRawEmailLogin() starting...", {
    email,
    pwLen: password.length
  });

  try {
    // Step 1: Check Firebase Init Service
    console.log("🐛 [Step 1] Waiting for Firebase to be ready...");
    await this.firebaseInit.waitForReady();
    console.log("✅ [Step 1] Firebase init service reports ready");

    // Step 2: Try to get Firebase App
    console.log("🐛 [Step 2] Getting Firebase App...");
    let app;
    try {
      app = getApp();
      console.log("✅ [Step 2] Firebase app retrieved:", app.name);
    } catch (appError) {
      console.error("❌ [Step 2] Failed to get Firebase app:", appError);
      throw new Error("Firebase app not initialized");
    }

    // Step 3: Get Auth instance
    console.log("🐛 [Step 3] Getting Auth instance...");
    const auth = getAuth(app);
    console.log("✅ [Step 3] Auth instance retrieved");
    console.log("🐛 [Step 3] auth.currentUser BEFORE:", auth.currentUser?.uid || "null");
    console.log("🐛 [Step 3] auth.config:", {
      apiKey: auth.config.apiKey?.substring(0, 10) + "...",
      authDomain: auth.config.authDomain
    });

    // Step 4: Attempt sign in
    console.log("🐛 [Step 4] Calling signInWithEmailAndPassword...");
    console.log("🐛 [Step 4] Email:", email);
    console.log("🐛 [Step 4] Password length:", password.length);
    
    //const cred = await signInWithEmailAndPassword(auth, email, password);
    /*const cred = await this.firebaseInit.safeFirebaseCall(
      async () => signInWithEmailAndPassword(auth, email, password)
    );*/
    const cred = await FirebaseAuthentication.signInWithEmailAndPassword({email, password});
    console.log("✅ [Step 4] signInWithEmailAndPassword SUCCESS!");
    console.log("✅ [Step 4] User ID:", cred.user.uid);
    console.log("✅ [Step 4] User email:", cred.user.email);
    console.log("🐛 [Step 4] auth.currentUser AFTER:", auth.currentUser?.uid || "null");

  } catch (err: any) {
    console.log("❌ [AuthService] debugRawEmailLogin ERROR");
    console.log("❌ Error type:", typeof err);
    console.log("❌ Error constructor:", err?.constructor?.name);
    console.log("❌ Error code:", err?.code);
    console.log("❌ Error message:", err?.message);
    console.log("❌ Full error object:", JSON.stringify(err, null, 2));
    console.log("❌ Error stack:", err?.stack);
    
    // Additional checks
    if (err?.code) {
      switch (err.code) {
        case 'auth/invalid-email':
          console.log("💡 Email format is invalid");
          break;
        case 'auth/user-not-found':
          console.log("💡 User does not exist");
          break;
        case 'auth/wrong-password':
          console.log("💡 Password is incorrect");
          break;
        case 'auth/network-request-failed':
          console.log("💡 Network error - check internet connection");
          break;
        case 'auth/too-many-requests':
          console.log("💡 Too many failed login attempts");
          break;
        default:
          console.log("💡 Unknown error code:", err.code);
      }
    }
  }
}

/** Authenticates the user by email OR username + password.
 *
 * @param args - { email (or username), password }
 * @returns Firebase UserCredential + optional loginCode
 */
public async signInWithEmail(args: { email: string; password: string }): 
  Promise<{ userCredential: UserCredential }> {

  await this.firebaseInit.waitForReady();

  let email = args.email;

  // Username → Email
  if (!args.email.includes("@")) {
    console.log("🔎 Resolving username to email...", args.email);
    const response: any = await this.http.post("user/get-email", { username: args.email }).toPromise();
    if (!response?.email) {
      const err: any = new Error("Benutzername existiert nicht.");
      err.code = "auth/user-not-found";
      throw err;
    }
    email = response.email;
  }

  console.log("🔐 Signing in with email (native for iOS):", email);

  let userCredential: UserCredential;

  const isNative =
    this.platform.is('ios') ||
    this.platform.is('capacitor') ||
    this.platform.is('cordova');

  if (isNative) {
    console.log("📱 Using NATIVE FirebaseAuthentication email login");

    const result = await FirebaseAuthentication.signInWithEmailAndPassword({
      email,
      password: args.password,
    });

    if (!result?.user) {
      throw new Error("Native sign-in returned no user");
    }

    const nu = result.user;

    // Fake-Web-User bauen:
    const webUser: any = {
      uid: nu.uid,
      email: nu.email,
      displayName: nu.displayName,
      photoURL: nu.photoUrl,
      emailVerified: nu.emailVerified,
      isAnonymous: false,
      providerData: nu.providerData || []
    };

    userCredential = {
      user: webUser,
      providerId: "password",
      operationType: "signIn"
    } as UserCredential;

  } else {
    // Standard Web Login
    const auth = await this.getFirebaseAuth();
    userCredential = await signInWithEmailAndPassword(auth, email, args.password);
  }

  console.log("✅ Email login SUCCESS:", userCredential.user.uid);

  // Set local state
  this.setUserInInterceptor(userCredential.user);
  this.fireUser = userCredential.user;

  try {
    await this.userService.setCurrentUser(userCredential.user);
  } catch (error: any) {
    // If backend rejects the user (e.g., license blocked), sign out from Firebase
    console.error("❌ Backend rejected user, signing out from Firebase:", error);
    
    // Sign out from Firebase
    if (isNative) {
      await FirebaseAuthentication.signOut();
    } else {
      const auth = await this.getFirebaseAuth();
      await auth.signOut();
    }
    
    // Clear local state
    this.setUserInInterceptor(null);
    this.fireUser = undefined;
    
    // Re-throw error so it can be handled by the UI
    throw error;
  }

  return { userCredential };
}


  /**
   * Let the user sign is as a guest. This should result in the user having less options.
   */
  public async signInWithGuest(): Promise<UserCredential> {
    // Wait for Firebase to be ready
    await this.firebaseInit.waitForReady();
    
    await this.dailyStreak.setDefaultAnon();
    
    const userCredential = await this.firebaseInit.safeFirebaseCall(
      async () => signInAnonymously(await this.getFirebaseAuth())
    );
    
    if (!userCredential) {
      throw new Error("Gast-Anmeldung fehlgeschlagen - Firebase nicht bereit");
    }

    // Set user in interceptor for HTTP requests
    this.setUserInInterceptor(userCredential.user);
    
    // Also set the fireUser for immediate use
    this.fireUser = userCredential.user;
    
    return userCredential;
  }

  /**
   * Registers a user with a mail address and password.
   * If the user is currently signed in as a guest, the new credentials will be linked.
   *
   * @param email    - The users mail address.
   * @param password - The password to be hashed by the server.
   * @returns the created user if one is created or null if the username is already taken.
   */
  public async registerUser(user: {
    email: string,
    password: string,
    username: string,
    age: number,
    onboarding: any,
    promocode?: string,
    name: string,
  }): Promise<UserCredential | null> {
    // Race Condition Protection: Verhindere gleichzeitige Registrierungen
    if (this.isRegistering) {
      console.warn('⚠️ [AuthService] Registrierung bereits in Bearbeitung, ignoriere doppelten Aufruf');
      const err: any = new Error("Registrierung läuft bereits. Bitte warten Sie einen Moment.");
      err.code = "auth/operation-in-progress";
      throw err;
    }
    
    this.isRegistering = true;
    
    try {
      await this.firebaseInit.waitForReady();
    
      // Username-Check
      if (await this.userService.doesUsernameExist(user.username)) {
        this.isRegistering = false;
        return null;
      }
  
    // E-Mail-Prüfung: Prüfe ob E-Mail bereits in Firebase existiert
    try {
      const auth = await this.getFirebaseAuth();
      const signInMethods = await fetchSignInMethodsForEmail(auth, user.email);
      
      if (signInMethods.length > 0) {
        // E-Mail existiert bereits - versuche stattdessen einzuloggen
        console.warn('⚠️ [AuthService] E-Mail bereits registriert, versuche Login:', user.email);
        const err: any = new Error("Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich an.");
        err.code = "auth/email-already-in-use";
        throw err;
      }
    } catch (error: any) {
      // Wenn es ein Firebase-Fehler ist, weiterwerfen
      if (error.code === "auth/email-already-in-use") {
        throw error;
      }
      // Andere Fehler (z.B. Netzwerk) ignorieren und fortfahren
      console.warn('⚠️ [AuthService] E-Mail-Prüfung fehlgeschlagen, fortfahren:', error);
    }
  
    const isNative = this.platform.is('capacitor') || this.platform.is('cordova');
    let userCredentials: UserCredential | null = null;
  
    // 1) Anonymer Account → linkAccount wie bisher
    if (this.fireUser?.isAnonymous) {
      userCredentials = await this.linkAccount(user.email, user.password);
  
    // 2) Native iOS/Android → NATIVE createUserWithEmailAndPassword
    } else if (isNative) {
      console.log('📱 [AuthService] Native email SIGNUP');
  
      try {
        const result = await FirebaseAuthentication.createUserWithEmailAndPassword({
          email: user.email,
          password: user.password,
        });
  
        if (!result?.user) {
          throw new Error('Native sign-up returned no user');
        }
  
        const nu = result.user;
  
        // "Fake" Web-User bauen, damit der Rest der App weiter mit User arbeitet
        const webUser: any = {
          uid: nu.uid,
          email: nu.email,
          displayName: nu.displayName,
          photoURL: nu.photoUrl,
          emailVerified: nu.emailVerified,
          isAnonymous: false,
          providerData: nu.providerData || [],
        };
  
        userCredentials = {
          user: webUser,
          providerId: 'password',
          operationType: 'signIn',
        } as UserCredential;
      } catch (nativeError: any) {
        // Firebase-Fehler weiterwerfen
        if (nativeError.code === 'auth/email-already-in-use') {
          const err: any = new Error("Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich an.");
          err.code = "auth/email-already-in-use";
          throw err;
        }
        throw nativeError;
      }
  
    // 3) Browser/Web → wie bisher
    } else {
      try {
        userCredentials = await this.firebaseInit.safeFirebaseCall(
          async () => createUserWithEmailAndPassword(
            await this.getFirebaseAuth(),
            user.email,
            user.password
          ),
        );
      } catch (webError: any) {
        // Firebase-Fehler weiterwerfen
        if (webError.code === 'auth/email-already-in-use') {
          const err: any = new Error("Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich an.");
          err.code = "auth/email-already-in-use";
          throw err;
        }
        throw webError;
      }
    }
  
      if (userCredentials?.user) {
        // Lokalen State setzen
        this.setUserInInterceptor(userCredentials.user);
        this.fireUser = userCredentials.user;
    
        // Analytics + Backend-User
        this.analyticsService.trackEvent('Sign Up', { provider: 'Email' });
        this.analyticsService.trackConversion('signup', 0);
    
        const dbUser = await this.userService.createUserInBackend(
          userCredentials.user,
          user.username,
          user.age,
          user.onboarding,
        );
    
        await this.userService.setCurrentUser(dbUser);
        // Optional: Verification-Mail später wieder aktivieren
        // await this.sendVerificationMail(userCredentials.user, true);
      }
    
      return userCredentials;
    } catch (error) {
      // Stelle sicher, dass isRegistering auch bei Fehlern zurückgesetzt wird
      this.isRegistering = false;
      throw error;
    } finally {
      // Stelle sicher, dass isRegistering immer zurückgesetzt wird
      this.isRegistering = false;
    }
  }  

  /**
   * Links an anonymous account to the given credentials.
   */
  public async linkAccount(email: string, password: string): Promise<UserCredential | null> {
    // Wait for Firebase to be ready
    await this.firebaseInit.waitForReady();
    
    const credential = EmailAuthProvider.credential(
      email,
      password
    );

    try {
      await this.analyticsService.trackEvent("Link Account");
      
      const result = await this.firebaseInit.safeFirebaseCall(
        () => this.fireUser ? linkWithCredential(this.fireUser, credential) : null
      );
      
      return result ?? null;
    } catch (e) {
      this.showToast();
    }
    return null;
  }

  /**
   * Reloads the current user to get the latest verification status
   */
  public async reloadUser(): Promise<void> {
    try {
      const auth = await this.getFirebaseAuth();
      const user = auth.currentUser;
      
      if (user) {
        await reload(user);
        // Update fireUser with refreshed data
        this.fireUser = {
          ...this.fireUser,
          emailVerified: user.emailVerified
        } as any;
      }
    } catch (error) {
      console.error('Error reloading user:', error);
    }
  }

  /**
   * Email verification when new user registers
   */
  public async sendVerificationMail(user: User, preventNavigation = false): Promise<void> {
    try {
      await this.firebaseInit.safeFirebaseCall(
        () => this.fireUser ? sendEmailVerification(this.fireUser) : null
      );
      if (!preventNavigation) {
        this.router.navigate(["verifyMail"]);
      }
    } catch (error) {
      this.showToast();
    }
  }

  /**
   * Starts the password recovery.
   *
   * @param passwordResetEmail - The email-address to send the recover password mail to.
   */
  public async recoverPassword(passwordResetEmail: string): Promise<String> {
    try {
      await this.firebaseInit.safeFirebaseCall(
        async () => sendPasswordResetEmail(await this.getFirebaseAuth(), passwordResetEmail)
      );
      this.router.navigateByUrl("login", {replaceUrl: true});
      return "success";
    } catch (error) {
      return "error";
    }
  }

  /**
   * Changes the password of the user.
   *
   * @param email - The email of the user.
   * @param newPassword - The new password.
   */
  public async changePassword(newPassword: string): Promise<string> {
    // Wait for Firebase to be ready
    await this.firebaseInit.waitForReady();
    
    const auth = await this.getFirebaseAuth();
    const user = auth.currentUser;
  
    try {
      if (!user) throw new Error("Kein Benutzer eingeloggt.");
      
      await this.firebaseInit.safeFirebaseCall(
        () => updatePassword(user, newPassword)
      );
      
      await this.userService.updateUser({ loginCode: null });
      return "success";
    } catch (error) {
      console.error("❌ Fehler beim Passwort ändern:", error);
      return "error";
    }
  }  

  /**
   * Opens a Gmail login prompt.
   */
  public async signInWithGoogle(): Promise<void> {
    
    // Check if we're in a native Capacitor app
    const isNativeApp = (this.platform.is("capacitor") || this.platform.is("cordova")) && !this.platform.is("mobileweb");
    
    // Alternative detection: Check if we're in a browser
    const isInBrowser = window.location.protocol === 'http:' || window.location.protocol === 'https:';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Force native app detection if we're not in a browser
    const forceNativeApp = !isInBrowser || (isInBrowser && isLocalhost && this.platform.is("android"));
    
    // Fallback: If we're on Android and not in a browser, force native
    const isAndroid = this.platform.is("android");
    const isInRealBrowser = isInBrowser && !isLocalhost;
    const shouldUseNative = isNativeApp || forceNativeApp || (isAndroid && !isInRealBrowser);
    
    // Emergency fallback: If we're on Android (detected by user agent), force native
    const isAndroidByUserAgent = navigator.userAgent.toLowerCase().includes('android');
    
    // Final decision with emergency fallback
    const finalShouldUseNative = shouldUseNative || (isAndroidByUserAgent && !isInRealBrowser);
    
    // FORCE NATIVE FOR ANDROID - Emergency override
    if (isAndroidByUserAgent) {
      try {
        return await this.nativeGoogleSignIn();
      } catch (error) {
        console.error("❌ NATIVE GOOGLE SIGN IN FAILED ❌:", error);
        this.showToast("Google-Anmeldung fehlgeschlagen: " + error);
        return;
      }
    }
    
    if (finalShouldUseNative) {
      // Skip Firebase readiness check and try direct sign in
      try {
        return await this.nativeGoogleSignIn();
      } catch (error) {
        console.error("❌ NATIVE GOOGLE SIGN IN FAILED ❌:", error);
        this.showToast("Google-Anmeldung fehlgeschlagen: " + error);
        return;
      }
    } else {
      // For web (including mobile web), use AngularFire directly
      try {
        // Use modular Firebase GoogleAuthProvider
        const provider = new GoogleAuthProvider();
        return await this.signInWithAuthProvider(provider);
      } catch (error) {
        console.error("Failed to create GoogleAuthProvider:", error);
        this.showToast("Google-Anmeldung fehlgeschlagen: " + error);
        return;
      }
    }
  }

  public async nativeGoogleSignIn(): Promise<void> {
    try {
      // Direct Firebase call with error handling
      const result = await FirebaseAuthentication.signInWithGoogle();
      
      // Check if result and credential are valid
      if (!result || !result.credential || !result.credential.idToken) {
        console.error("Invalid Google Sign In result");
        this.showToast("Google-Anmeldung fehlgeschlagen - Ungültige Anmeldedaten");
        return;
      }
      
      // The user is already signed in via Capacitor Firebase Authentication
      // We don't need to call firebase.auth().signInWithCredential() again
      // Just wait a bit for the auth state to propagate
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Get the current user from Capacitor Firebase
      try {
        const userResult = await FirebaseAuthentication.getCurrentUser();
        if (userResult && userResult.user) {
          const user = userResult.user;
          
          // Prüfe ob ein Account mit dieser E-Mail bereits existiert (aber andere UID)
          if (user.email) {
            try {
              const auth = await this.getFirebaseAuth();
              const signInMethods = await fetchSignInMethodsForEmail(auth, user.email);
              
              // Wenn es andere Sign-In-Methoden gibt (z.B. password), könnte es ein Duplikat sein
              if (signInMethods.length > 0 && signInMethods.includes('password')) {
                console.warn('⚠️ [AuthService] E-Mail bereits mit Password-Account registriert:', user.email);
                // Versuche den bestehenden Account zu finden und zu verlinken
                // Da wir bereits eingeloggt sind, können wir nur warnen
                // In Zukunft könnte man hier einen Link-Flow implementieren
              }
            } catch (emailCheckError) {
              console.warn('⚠️ [AuthService] E-Mail-Prüfung fehlgeschlagen:', emailCheckError);
            }
          }
          
          // Set the fireUser for compatibility
          this.fireUser = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoUrl,
            emailVerified: user.emailVerified,
            isAnonymous: false
          } as any;
          
          // CRITICAL: Set user in interceptor FIRST before making API calls
          this.setUserInInterceptor(this.fireUser);
          
          // Load backend user data immediately for navigation
          try {
            await this.userService.setCurrentUser(this.fireUser);
            console.log('✅ Backend user loaded after Google login, user$ emitted');
          } catch (error) {
            console.error('❌ Error loading backend user after Google login:', error);
          }
          
          //User wants to reauthenticate
          if (this.reauth) {
            console.log("Reauthenticating user...");
            // Reauthentication is handled by Capacitor Firebase
          }

          if (result.additionalUserInfo?.isNewUser) {
            console.log("New user detected");
            this.analyticsService.trackEvent("Sign Up", {"provider": "Google"});
            this.router.navigate(["username"]);
            return;
          }
          else {
            console.log("Existing user detected");
            
            // Check if user exists in backend, if not, create them
            try {
              const backendUser = await this.userService.getUserApi();
              console.log("Backend user found:", backendUser.uid);
            } catch (backendError: any) {
              if (backendError.status === 404 || backendError.status === 403) {
                console.log("User not found in backend, creating user...");
                // Create user in backend
                const newUser = await this.userService.createUserInBackend(
                  this.fireUser,
                  user.email?.split('@')[0] || 'user',
                  0,
                  undefined,
                  undefined
                );
                console.log("Backend user created:", newUser.uid);
              } else {
                console.error("Error checking backend user:", backendError);
              }
            }
            
            this.analyticsService.trackEvent("Sign In", {"provider": "Google"});
            this.router.navigate(["tabs/home"], {replaceUrl: true});
          }
        } else {
          console.error("No user found after sign in");
          this.showToast("Anmeldung fehlgeschlagen - Benutzer nicht gefunden");
        }
      } catch (userError) {
        console.error("Error getting current user:", userError);
        this.showToast("Fehler beim Abrufen der Benutzerdaten");
      }
    } catch(error) {
      console.error("Google Sign In error:", error);
      this.showToast("Google-Anmeldung fehlgeschlagen: " + error);
    }
  }

  /**
   * Base function for singing in with the different auth providers (Facebook, Microsoft, Gmail, Github).
   *
   * @param provider - The provider to be used for authentication.
   */
  private async signInWithAuthProvider(provider: any): Promise<void> {
    try {
      console.log("Attempting web Google Sign In with AngularFire...");
      
      // For web, always try popup first, only use redirect as fallback
      let userCredential;
      try {
        console.log("Trying modular Firebase popup sign in...");
        userCredential = await signInWithPopup(await this.getFirebaseAuth(), provider);
        console.log("Modular Firebase popup sign in successful");
      } catch (popupError) {
        console.warn("Modular Firebase popup sign in failed:", popupError);
        
        // Only use redirect as fallback for web browsers, not for mobile apps
        if ((this.platform.is('capacitor') || this.platform.is('cordova')) && !this.platform.is('mobileweb')) {
          console.error("Popup failed on mobile, this should not happen");
          this.showToast("Google-Anmeldung fehlgeschlagen: " + popupError);
          return;
        }
        
        try {
          // Fallback to redirect method only for web browsers
          console.log("Trying modular Firebase redirect sign in...");
          await signInWithRedirect(await this.getFirebaseAuth(), provider);
          console.log("Modular Firebase redirect sign in initiated");
          return; // Redirect will handle the rest
        } catch (redirectError) {
          console.error("Both modular Firebase popup and redirect failed:", redirectError);
          this.showToast("Google-Anmeldung fehlgeschlagen: " + redirectError);
          return;
        }
      }
      
      // Check if userCredential is valid
      if (!userCredential || !userCredential.user) {
        console.error("Invalid user credential received");
        this.showToast("Anmeldung fehlgeschlagen - Ungültige Anmeldedaten");
        return;
      }
      
      console.log("User credential received:", userCredential.user.uid);
      
      // Prüfe ob ein Account mit dieser E-Mail bereits existiert (aber andere UID)
      if (userCredential.user.email) {
        try {
          const auth = await this.getFirebaseAuth();
          const signInMethods = await fetchSignInMethodsForEmail(auth, userCredential.user.email);
          
          // Wenn es andere Sign-In-Methoden gibt (z.B. password), könnte es ein Duplikat sein
          if (signInMethods.length > 0 && signInMethods.includes('password')) {
            console.warn('⚠️ [AuthService] E-Mail bereits mit Password-Account registriert:', userCredential.user.email);
            // Versuche den bestehenden Account zu finden und zu verlinken
            // Da wir bereits eingeloggt sind, können wir nur warnen
            // In Zukunft könnte man hier einen Link-Flow implementieren
          }
        } catch (emailCheckError) {
          console.warn('⚠️ [AuthService] E-Mail-Prüfung fehlgeschlagen:', emailCheckError);
        }
      }
      
      // Set user in interceptor
      this.setUserInInterceptor(userCredential.user);
      this.fireUser = userCredential.user;
      
      // Load backend user data and emit via user$ observable for navigation
      try {
        await this.userService.setCurrentUser(userCredential.user);
        console.log('✅ Backend user loaded after provider login, user$ emitted');
      } catch (error) {
        console.error('❌ Error loading backend user after provider login:', error);
      }
      
      //User wants to reauthenticate
      if (this.reauth && userCredential.credential) {
        console.log("Reauthenticating user...");
        if (this.fireUser) {
          await reauthenticateWithCredential(this.fireUser, userCredential.credential);
        }
      }
      
      if (userCredential.additionalUserInfo?.isNewUser) {
        console.log("New user detected");
        this.analyticsService.trackEvent("Sign Up", {"provider": provider.providerId});
        this.router.navigate(["username"], {replaceUrl: true});
        return;
      } else {
        console.log("Existing user detected");
        this.analyticsService.trackEvent("Sign In", {"provider": provider.providerId});
        this.router.navigate(["tabs/home"], {replaceUrl: true});
      }
    } catch (e) {
      console.error("Sign in with provider failed:", e);
      this.showToast("Fehler: " + e);
    }
  }

  public async signInWithApple(): Promise<void> {
    if (!this.platform.is('ios')) {
      this.showToast('Apple Sign In ist nur auf iOS verfügbar.');
      return;
    }
  
    try {
      console.log('🍎 [AuthService] Starting Apple Sign In via FirebaseAuthentication (native auth)...');
  
      // Wichtig: KEIN skipNativeAuth hier
      const result = await FirebaseAuthentication.signInWithApple();
  
      console.log('🍎 [AuthService] Native Apple result:', JSON.stringify(result, null, 2));
  
      // Wenn wir hier sind, hat das native Firebase-SDK den User bereits eingeloggt.
      const current = await FirebaseAuthentication.getCurrentUser();
  
      if (!current || !current.user) {
        console.error('❌ [AuthService] No native Firebase user after Apple sign-in');
        this.showToast('Apple-Anmeldung fehlgeschlagen – kein Benutzer vorhanden.');
        return;
      }
  
      const u = current.user;
      console.log('🍎 [AuthService] Native Firebase user after Apple sign-in:', u.uid);
  
      // Prüfe ob ein Account mit dieser E-Mail bereits existiert (aber andere UID)
      if (u.email) {
        try {
          const auth = await this.getFirebaseAuth();
          const signInMethods = await fetchSignInMethodsForEmail(auth, u.email);
          
          // Wenn es andere Sign-In-Methoden gibt (z.B. password), könnte es ein Duplikat sein
          if (signInMethods.length > 0 && signInMethods.includes('password')) {
            console.warn('⚠️ [AuthService] E-Mail bereits mit Password-Account registriert:', u.email);
            // Versuche den bestehenden Account zu finden und zu verlinken
            // Da wir bereits eingeloggt sind, können wir nur warnen
            // In Zukunft könnte man hier einen Link-Flow implementieren
          }
        } catch (emailCheckError) {
          console.warn('⚠️ [AuthService] E-Mail-Prüfung fehlgeschlagen:', emailCheckError);
        }
      }
  
      // fireUser als "synthetischen" Web-User setzen
      this.fireUser = {
        uid: u.uid,
        email: u.email,
        displayName: u.displayName,
        photoURL: u.photoUrl,
        emailVerified: u.emailVerified,
        isAnonymous: false,
      } as any;
  
      this.setUserInInterceptor(this.fireUser);

      await this.userService.setCurrentUser(this.fireUser);
  
      // Backend-User laden / anlegen wie bei Google
      try {
        const backendUser = await this.userService.getUserApi();
        console.log('🍎 Existing backend user after Apple sign-in:', backendUser.uid);
        this.analyticsService.trackEvent('Sign In', { provider: 'Apple' });
        this.router.navigate(['tabs/home'], { replaceUrl: true });
      } catch (backendError: any) {
        if (backendError.status === 404 || backendError.status === 403) {
          console.log('🍎 New user detected after native Apple sign-in, creating in backend...');
          const username = (u.email?.split('@')[0]) || 'user';
          const newUser = await this.userService.createUserInBackend(
            this.fireUser!,
            username,
            0,
            undefined,
            undefined
          );
          console.log('🍎 Backend user created:', newUser.uid);
          this.analyticsService.trackEvent('Sign Up', { provider: 'Apple' });
          this.router.navigate(['username'], { replaceUrl: true });
        } else {
          console.error('🍎 Error checking backend user:', backendError);
          this.showToast('Fehler beim Überprüfen des Benutzers');
        }
      }
  
    } catch (err: any) {
      console.error('❌ [AuthService] Apple Sign In (native) error:', err);
      this.showToast('Apple-Anmeldung fehlgeschlagen: ' + (err?.message || err));
    }
  }

  generateCodeVerifier(length: number = 128): string {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
  
    // Wandeln Sie das Array in eine Base64URL-kodierte Zeichenkette um
    const codeVerifier = this.base64UrlEncode(array);
  
    // Stellen Sie sicher, dass der Code Verifier die richtige Länge hat
    // Der Code Verifier muss zwischen 43 und 128 Zeichen lang sein
    if (codeVerifier.length < 43) {
      // Wiederholen, bis die Mindestlänge erreicht ist
      return this.generateCodeVerifier(length + 10);
    } else if (codeVerifier.length > 128) {
      // Schneiden Sie auf 128 Zeichen zu
      const truncatedVerifier = codeVerifier.substr(0, 128);
      this.codeVerifier = truncatedVerifier;
      localStorage.setItem('codeVerifier', truncatedVerifier);
      return truncatedVerifier;
    } else {
      this.codeVerifier = codeVerifier;
      localStorage.setItem('codeVerifier', codeVerifier);
      return codeVerifier;
    }
  }
  
  async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const base64Digest = this.base64UrlEncode(digest);
    return base64Digest;
  }  

  base64UrlEncode(arrayBuffer: ArrayBuffer | Uint8Array): string {
    let bytes: Uint8Array;
    if (arrayBuffer instanceof ArrayBuffer) {
      bytes = new Uint8Array(arrayBuffer);
    } else {
      bytes = arrayBuffer;
    }
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64String = window.btoa(binary);
    // Base64URL-Encodierung
    return base64String
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }  

  public async signInWithEduplaces()
  {
    console.log('📱 [AuthService] Starting Eduplaces Sign In...');
    
    // Wait for Firebase to be ready
    await this.firebaseInit.waitForReady();
    
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    const clientId = environment.eduplacesClientId;
    let redirectUri = 'grammario://callback';
    const scope = 'openid school school_name school_location school_type school_official_id role groups';
    const state = Math.random().toString(36).substring(2);
    const authorizationEndpoint = environment.eduplacesUrl;

    let url =
    `${authorizationEndpoint}?response_type=code&client_id=${encodeURIComponent(clientId)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}` +
    `&state=${encodeURIComponent(state)}&code_challenge=${encodeURIComponent(codeChallenge)}` +
    `&code_challenge_method=S256`;

    // Speichern Sie den Code Verifier und den State
    localStorage.setItem('codeVerifier', codeVerifier);
    localStorage.setItem('oauthState', state);
    
    console.log('📱 [AuthService] Eduplaces OAuth state saved:', state);
    console.log('📱 [AuthService] Redirect URI:', redirectUri);

    if (this.platform.is('capacitor') || this.platform.is('cordova')) {
      // Für Capacitor
      console.log('📱 [AuthService] Opening Eduplaces in Browser...');
      console.log('📱 [AuthService] URL:', url.substring(0, 100) + '...');
      await Browser.open({ url });
      console.log('📱 [AuthService] Browser opened, waiting for callback...');
    }
    else {
      // Für den Webbrowser
      redirectUri = 'https://sso.grammario.de/callback';
      url =
      `${authorizationEndpoint}?response_type=code&client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}` +
      `&state=${encodeURIComponent(state)}&code_challenge=${encodeURIComponent(codeChallenge)}` +
      `&code_challenge_method=S256`;
      console.log('📱 [AuthService] Redirecting to Eduplaces (web)...');
      window.location.href = url;
    }
  }

  public async reauthenticateWithEduplaces() {
    // Wait for Firebase to be ready
    await this.firebaseInit.waitForReady();
    
    const auth = await this.getFirebaseAuth();
    const user = auth.currentUser;
  
    if (user) {
      try {
        // Erstellen Sie einen neuen eduplaces OAuth Provider
        const provider = new OAuthProvider('oidc.eduplaces');
  
        // Optional: Scopes hinzufügen
        provider.addScope('openid school school_name school_location school_type school_official_id role groups');
  
        // Re-Authentifizierung mit Popup - use safe Firebase call
        await this.firebaseInit.safeFirebaseCall(
          () => reauthenticateWithCredential(user, provider.credential({}))
        );
      }
      catch (error) {
        console.error('Fehler bei der Re-Authentifizierung:', error);
      }
    }
  }

  async handleAuthCallback(code: string, state: string) {
    console.log('📱 [AuthService] handleAuthCallback called');
    console.log('📱 [AuthService] Code:', code ? 'present (' + code.substring(0, 10) + '...)' : 'missing');
    console.log('📱 [AuthService] State:', state);
    
    // Wait for Firebase to be ready
    await this.firebaseInit.waitForReady();
    
    const savedState = localStorage.getItem('oauthState');
    const codeVerifier = localStorage.getItem('codeVerifier');
    
    console.log('📱 [AuthService] Saved state:', savedState);
    console.log('📱 [AuthService] Code verifier:', codeVerifier ? 'present' : 'missing');
  
    if (state !== savedState) {
      console.error('📱 [AuthService] Ungültiger State - received:', state, 'expected:', savedState);
      return;
    }
  
    if (!code || !codeVerifier) {
      console.error('📱 [AuthService] Fehlender Autorisierungscode oder Code Verifier');
      return;
    }
    
    console.log('📱 [AuthService] Closing browser...');
    await Browser.close();
    
    // Senden Sie den Autorisierungscode und den Code Verifier an Ihr PHP-Backend
    try {
      console.log('📱 [AuthService] Sending callback to backend...');
      const response: any = await this.http.post('auth/eduplaces/callback', {
        code,
        codeVerifier
      }).toPromise();

      const customToken: string | undefined = response?.customToken ?? response?.token;
      if (!customToken) {
        console.error('📱 [AuthService] Missing custom token in backend response:', response);
        return;
      }
      
      let userCredential: UserCredential;
      
      const isNative =
        this.platform.is('ios') ||
        this.platform.is('capacitor') ||
        this.platform.is('cordova');
      
      if (isNative) {
        console.log("📱 Using NATIVE FirebaseAuthentication custom token login");
        
        const result = await FirebaseAuthentication.signInWithCustomToken({ token: customToken });
        
        if (!result?.user) {
          console.error("Native custom token sign-in returned no user");
          return;
        }
        
        const nu = result.user;
        
        // Fake-Web-User bauen:
        const webUser: any = {
          uid: nu.uid,
          email: nu.email,
          displayName: nu.displayName,
          photoURL: nu.photoUrl,
          emailVerified: nu.emailVerified,
          isAnonymous: false,
          providerData: nu.providerData || []
        };
        
        userCredential = {
          user: webUser,
          providerId: "custom",
          operationType: "signIn"
        } as UserCredential;
        
        // Set user in interceptor and fireUser
        this.setUserInInterceptor(webUser);
        this.fireUser = webUser;
        
        // Load backend user data
        try {
          await this.userService.setCurrentUser(webUser);
          console.log('✅ Backend user loaded after native custom token login');
        } catch (error) {
          console.error('❌ Error loading backend user after native custom token login:', error);
        }
      } else {
        // Standard Web Login
        userCredential = await this.firebaseInit.safeFirebaseCall(
          async () => signInWithCustomToken(await this.getFirebaseAuth(), customToken)
        ) as UserCredential;
        
        if (!userCredential) {
          console.error("Eduplaces sign in failed - Firebase not ready");
          return;
        }
        
        // Set user in interceptor and fireUser
        this.setUserInInterceptor(userCredential.user);
        this.fireUser = userCredential.user;
        
        // Load backend user data
        try {
          await this.userService.setCurrentUser(userCredential.user);
          console.log('✅ Backend user loaded after web custom token login');
        } catch (error) {
          console.error('❌ Error loading backend user after web custom token login:', error);
        }
      }
      
      const isNativePlatform = this.platform.is('capacitor') || this.platform.is('cordova');
      
      if (isNativePlatform) {
        // On native platforms, use Capacitor Firebase plugin for custom token sign in
        console.log('📱 [AuthService] Signing in with custom token (native)...');
        
        const result = await FirebaseAuthentication.signInWithCustomToken({ token: customToken });
        
        if (!result || !result.user) {
          console.error("📱 [AuthService] Eduplaces sign in failed - No user data");
          return;
        }
        
        console.log('📱 [AuthService] Firebase sign in successful (native):', result.user.uid);
        
        // Create a user object compatible with Firebase User type
        this.fireUser = {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoUrl,
          emailVerified: result.user.emailVerified,
          isAnonymous: false
        } as any;
        
        // CRITICAL: Cache the token for iOS
        try {
          const tokenRes = await FirebaseAuthentication.getIdToken({ forceRefresh: false });
          if (tokenRes?.token) {
            this.cachedNativeToken = tokenRes.token;
            this.cachedTokenExpiry = Date.now() + (55 * 60 * 1000);
            console.log('✅ [AuthService] Cached native token after Eduplaces Sign In');
          }
        } catch (tokenError) {
          console.warn('⚠️ [AuthService] Could not cache token after Eduplaces login:', tokenError);
        }
        
        this.setUserInInterceptor(this.fireUser);
        
        await this.userService.updateEduplacesUser(response.user);
        
        // For Eduplaces, always navigate to username page for new users or home for existing
        // Since we can't easily check isNewUser with native plugin, check if backend user exists
        try {
          await this.userService.setCurrentUser(this.fireUser);
          const backendUser = await this.userService.getUser();
          if (backendUser && backendUser.username) {
            this.analyticsService.trackEvent("Sign In", {"provider": "Eduplaces"});
            console.log('📱 [AuthService] Existing Eduplaces user, navigating to home');
            this.router.navigate(["tabs/home"], {replaceUrl: true});
          } else {
            this.analyticsService.trackEvent("Sign Up", {"provider": "Eduplaces"});
            console.log('📱 [AuthService] New Eduplaces user, navigating to username');
            this.router.navigate(["username"], {replaceUrl: true});
          }
        } catch (e) {
          // User doesn't exist in backend, treat as new user
          this.analyticsService.trackEvent("Sign Up", {"provider": "Eduplaces"});
          console.log('📱 [AuthService] New Eduplaces user (no backend user), navigating to username');
          this.router.navigate(["username"], {replaceUrl: true});
        }
      } else {
        // On web, use Web SDK
        console.log('📱 [AuthService] Signing in with custom token (web)...');
        let userCredential = await this.firebaseInit.safeFirebaseCall(
          async () => signInWithCustomToken(await this.getFirebaseAuth(), customToken)
        );
        
        if (!userCredential) {
          console.error("📱 [AuthService] Eduplaces sign in failed - Firebase not ready");
          return;
        }
        
        console.log('📱 [AuthService] Firebase sign in successful:', userCredential.user.uid);
        
        this.fireUser = userCredential.user;
        this.setUserInInterceptor(this.fireUser);
        
        await this.userService.updateEduplacesUser(response.user);
        
        // Check if this is a new user (simplified check)
        if(!this.fireUser || this.fireUser.uid !== userCredential.user.uid)
        {
          this.analyticsService.trackEvent("Sign Up", {"provider": "Eduplaces"});
          console.log('📱 [AuthService] New Eduplaces user, navigating to username');
          this.router.navigate(["username"], {replaceUrl: true});
          return;
        }
        else {
          this.analyticsService.trackEvent("Sign In", {"provider": "Eduplaces"});
          console.log('📱 [AuthService] Existing Eduplaces user, navigating to home');
          this.router.navigate(["tabs/home"], {replaceUrl: true});
        }
      }
      
      console.log("📱 [AuthService] Eduplaces user signed in successfully");
    } catch (error) {
      console.error("📱 [AuthService] Error during Eduplaces sign-in:", error);
    }
  }

  /**
   * Return the token of the current user.
   */
  public async getToken(forceRefresh = false): Promise<string | null> {
    await this.firebaseInit.waitForReady();
  
    if (this.platform.is('capacitor') || this.platform.is('cordova')) {
      try {
        if (!forceRefresh && this.cachedNativeToken && this.cachedTokenExpiry && Date.now() < this.cachedTokenExpiry) {
          return this.cachedNativeToken;
        }

        const result = await FirebaseAuthentication.getIdToken({ forceRefresh });
        const token = result.token || null;
        if (token) {
          this.cachedNativeToken = token;
          this.cachedTokenExpiry = Date.now() + (55 * 60 * 1000);
        }
        return token;
      } catch (e) {
        console.error("Native token error", e);
        return null;
      }
    }
  
    // Web fallback
    const user = (await this.getFirebaseAuth()).currentUser;
    return user ? await user.getIdToken(forceRefresh) : null;
  }
  
  
  /**
   * Sign out and remove the authToken from the localStorage.
   */
  public async signOut(): Promise<void> {
    try {
      console.log('🚪 [AuthService] Starting sign out process...');
  
      // 1. Lokalen State löschen
      this.userService.clearUser();   // <── WICHTIG
      this.fireUser = undefined;
      this.cachedNativeToken = undefined;
      this.cachedTokenExpiry = undefined;
      this.setUserInInterceptor(null);
  
      // 2. Native Logout (iOS/Android)
      if (this.platform.is("capacitor") || this.platform.is("cordova")) {
        console.log('📱 [AuthService] Signing out on native platform (Capacitor/Cordova)');
        try {
          await FirebaseAuthentication.signOut();
          this.router.navigate(["start"], {replaceUrl: true});
          console.log('✅ [AuthService] Native Firebase sign out successful');
        } catch (nativeError: any) {
          console.warn('⚠️ [AuthService] Native Firebase sign out error (continuing anyway):', nativeError?.message || nativeError);
        }
  
        try {
          await this.analyticsService.trackEvent("Sign Out");
          await this.analyticsService.signOut();
        } catch (analyticsError: any) {
          console.warn('⚠️ [AuthService] Analytics sign out error (continuing anyway):', analyticsError?.message || analyticsError);
        }
      }
  
      // 3. Web-Firebase signOut (fallback / Browser)
      await this.firebaseInit.safeFirebaseCall(
        async () => signOut(await this.getFirebaseAuth())
      );
      console.log('✅ [AuthService] Web Firebase sign out successful');
  
      // 4. localStorage
      localStorage.removeItem("onboarding");
      localStorage.removeItem("authToken");
  
      console.log('✅ [AuthService] Sign out complete, navigating to start page');
      this.router.navigate(["start"], { replaceUrl: true });
  
    } catch (err: any) {
      console.error('❌ [AuthService] Sign out error:', err?.message || err);
      this.showToast();
    }
  }
  
    /**
    * Checks if user exists in firestore
    */
    public async doesFireUserExist(user: User): Promise<boolean>
    {
        try {
          await this.firebaseInit.waitForReady();
          // Note: AngularFire Auth temporarily disabled due to initialization issues
          // Using direct Firebase Auth instead
          console.log("doesFireUserExist temporarily disabled - using direct Firebase Auth");
          return false;
        } catch (error) {
          console.warn("Firebase Auth not ready for user check:", error);
          return false;
        }
    }

  public async showToast(message?: string): Promise<void> {
    let toast = await this.toastController.create({
      message: message ?? "Ein Fehler ist aufgetreten.",
      duration: 3000,
      animated: true,
      position: "bottom",
    });
    await toast.present();
  }

  /**
   * Handle redirect result for web Google Sign In
   */
  public async handleRedirectResult(): Promise<void> {
    try {
      console.log("Checking for Firebase redirect result...");
      
      // Wait for Firebase to be ready first
      await this.firebaseInit.waitForReady();
      
      const result = await getRedirectResult(await this.getFirebaseAuth());
      if (result && result.user) {
        console.log("Firebase redirect sign in successful for user:", result.user.uid);
        
        //User wants to reauthenticate
        if (this.reauth) {
          console.log("Reauthenticating user from redirect...");
          if (this.fireUser) {
            // Note: Reauthentication with redirect result is complex in modular API
            // For now, we'll skip this specific case
            console.log("Reauthentication from redirect not implemented in modular API");
          }
        }
        
        // Check if this is a new user (simplified check)
        if(!this.fireUser || this.fireUser.uid !== result.user.uid) {
          console.log("New user from redirect");
          this.analyticsService.trackEvent("Sign Up", {"provider": "Google"});
          this.router.navigate(["username"], {replaceUrl: true});
        } else {
          console.log("Existing user from redirect");
          this.analyticsService.trackEvent("Sign In", {"provider": "Google"});
          this.router.navigate(["tabs/home"], {replaceUrl: true});
        }
      } else {
        console.log("No Firebase redirect result found");
      }
    } catch (error) {
      console.error("Firebase redirect result handling failed:", error);
      // Don't throw the error, just log it and continue
      // This prevents the app from crashing if Firebase isn't ready
    }
  }
}

